const PACKAGE_TAB_PREFIX = 'package:';

function resolveObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function resolveArray(value) {
  return Array.isArray(value) ? value : [];
}

function text(value) {
  return String(value ?? '').trim();
}

function firstText(...values) {
  return values.map((value) => text(value)).find(Boolean) ?? '';
}

function isInternalCode(value = '') {
  const normalized = text(value);
  return Boolean(normalized && /^[a-z0-9_.:-]+$/i.test(normalized) && /[_:.-]/.test(normalized));
}

function publicLabel(...values) {
  for (const value of values) {
    const label = text(value);
    if (label && !isInternalCode(label)) {
      return label;
    }
  }
  return '';
}

function listLines(values = []) {
  return resolveArray(values)
    .map((value) => `- ${text(value)}`)
    .filter((line) => line !== '-');
}

function fieldPackLines(fieldPacks = []) {
  return resolveArray(fieldPacks).flatMap((pack, index) => {
    const label = publicLabel(pack?.label, pack?.title, pack?.name) || `推荐字段包 ${index + 1}`;
    const fields = resolveArray(pack?.fields)
      .map((field) => fieldPackFieldLine(field))
      .filter(Boolean);
    return [
      `### ${label}`,
      ...fields,
    ].filter(Boolean);
  });
}

function fieldPackFieldLine(field = {}) {
  const item = resolveObject(field);
  const key = publicLabel(item.label, item.title, item.name, item.field_key, item.key);
  const value = text(item.value);
  if (!key) {
    return '';
  }
  return value ? `- ${key}：${value}` : `- ${key}`;
}

function actionLines(actions = []) {
  return resolveArray(actions)
    .map((action) => publicLabel(action?.label, action?.title, action?.name))
    .filter(Boolean)
    .map((label) => `- ${label}`);
}

function fieldPackApplyAction(pack = {}) {
  const item = resolveObject(pack);
  const applyAction = resolveObject(item.apply_action);
  const payload = resolveObject(applyAction.payload);
  const fieldPackId = firstText(item.field_pack_id, payload.field_pack_id);
  if (!fieldPackId) {
    return null;
  }
  const label = publicLabel(item.label, item.title, item.name) || '推荐字段包';
  return {
    ...applyAction,
    action_key: firstText(applyAction.action_key, 'apply_field_pack_suggestion'),
    key: `apply_field_pack_suggestion:${fieldPackId}`,
    label: `填入：${label}`,
    payload: {
      ...payload,
      field_pack_id: fieldPackId,
      target_package_id: firstText(item.target_package_id, payload.target_package_id),
      apply_scope: firstText(payload.apply_scope, 'current_artifact_draft'),
    },
  };
}

function fieldPackApplyActions(fieldPacks = []) {
  return resolveArray(fieldPacks).map(fieldPackApplyAction).filter(Boolean);
}

function section(title, lines = []) {
  const resolved = resolveArray(lines).filter(Boolean);
  return resolved.length ? [`## ${title}`, ...resolved] : [];
}

export function buildArtifactPackageTabKey(packageId = '') {
  const id = text(packageId);
  return id ? `${PACKAGE_TAB_PREFIX}${id}` : '';
}

export function isArtifactPackageTabKey(tabKey = '') {
  return text(tabKey).startsWith(PACKAGE_TAB_PREFIX);
}

export function artifactPackageIdFromTabKey(tabKey = '') {
  const key = text(tabKey);
  return isArtifactPackageTabKey(key) ? key.slice(PACKAGE_TAB_PREFIX.length) : '';
}

export function selectArtifactPackages(artifactDocument = {}) {
  return resolveArray(resolveObject(artifactDocument).procurement_packages)
    .map((item) => resolveObject(item))
    .filter((item) => text(item.package_id || item.id || item.key));
}

export function selectArtifactPackageTabs({
  artifactDocument = {},
  canvasTabCapabilities = {},
} = {}) {
  return selectArtifactPackages(artifactDocument).map((item, index) => {
    const packageId = text(item.package_id || item.id || item.key);
    const key = buildArtifactPackageTabKey(packageId);
    const capability = resolveObject(resolveObject(canvasTabCapabilities)[key]);
    const toolbarActions = resolveArray(capability.toolbar_actions || capability.toolbarActions);
    const applyActions = fieldPackApplyActions(item.field_pack_suggestions);
    const resolvedToolbarActions = [...toolbarActions, ...applyActions];
    const tab = {
      key,
      label: publicLabel(item.title, item.name, item.label) || `采购任务 ${index + 1}`,
    };
    return resolvedToolbarActions.length > 0 ? { ...tab, toolbar_actions: resolvedToolbarActions } : tab;
  });
}

function findPackageByTabKey(artifactDocument = {}, tabKey = '') {
  const packageId = artifactPackageIdFromTabKey(tabKey);
  if (!packageId) {
    return {};
  }
  return selectArtifactPackages(artifactDocument)
    .find((item) => text(item.package_id || item.id || item.key) === packageId) || {};
}

function revisionMatchesTab(revision = {}, tabKey = '') {
  const targetTab = text(tabKey) || 'requirement';
  const activeTab = text(resolveObject(revision).active_tab);
  if (targetTab === 'requirement') {
    return !activeTab || activeTab === 'requirement';
  }
  return activeTab === targetTab;
}

function isAcceptedRevision(revision = {}) {
  const item = resolveObject(revision);
  return item.accepted === true
    || text(item.status) === 'accepted'
    || Boolean(text(item.accepted_at));
}

function isRejectedRevision(revision = {}) {
  const item = resolveObject(revision);
  const status = text(item.status);
  return item.accepted === false
    || status === 'rejected'
    || status === 'not_admitted'
    || status === 'blocked'
    || status === 'unsupported';
}

function isAuthoritativeRevision(revision = {}) {
  const item = resolveObject(revision);
  const diff = text(item.diff);
  const sourceKind = text(item.source_kind);
  return !isRejectedRevision(item) && (
    isAcceptedRevision(item)
    || diff === 'model_generated'
    || diff === 'manual_edit'
    || diff === 'revision_rollback'
    || sourceKind === 'manual_canvas_save'
    || sourceKind === 'revision_rollback'
  );
}

function isTemplateProjectionRevision(revision = {}) {
  return text(resolveObject(revision).diff) === 'domain_template_projection';
}

function isManualRevision(revision = {}) {
  const item = resolveObject(revision);
  const diff = text(item.diff);
  const sourceKind = text(item.source_kind);
  return diff === 'manual_edit'
    || diff === 'revision_rollback'
    || sourceKind === 'manual_canvas_save'
    || sourceKind === 'revision_rollback';
}

function revisionVersion(revision = {}) {
  const version = Number(resolveObject(revision).version);
  return Number.isFinite(version) ? version : 0;
}

function latestAdmittedTemplateRevision(candidates = []) {
  const latestRevision = resolveArray(candidates)[0];
  if (!isTemplateProjectionRevision(latestRevision) || isRejectedRevision(latestRevision)) {
    return null;
  }
  return latestRevision;
}

function selectDocumentRevision(candidates = []) {
  const matchedCandidates = resolveArray(candidates);
  return [
    matchedCandidates.find((item) => isManualRevision(item) && !isRejectedRevision(item)),
    latestAdmittedTemplateRevision(matchedCandidates),
    matchedCandidates.find((item) => (
      isAuthoritativeRevision(item) && !isTemplateProjectionRevision(item)
    )),
    matchedCandidates.find((item) => isAuthoritativeRevision(item)),
    matchedCandidates.find((item) => !isTemplateProjectionRevision(item)),
    matchedCandidates[0],
  ].find(Boolean);
}

function latestRevisionContentForTab(options = {}) {
  const { artifactDocument, tabKey, versions } = resolveObject(options);
  const candidates = [
    ...resolveArray(versions),
    resolveObject(resolveObject(artifactDocument).latest_revision),
  ].filter((item) => Object.keys(resolveObject(item)).length > 0);
  const matchedCandidates = [...candidates]
    .filter((item) => revisionMatchesTab(item, tabKey));
  matchedCandidates.sort((left, right) => revisionVersion(right) - revisionVersion(left));
  const revision = selectDocumentRevision(matchedCandidates);
  return text(resolveObject(revision).content);
}

function packageMarkdown(packageItem = {}) {
  const title = publicLabel(packageItem.title, packageItem.name, packageItem.label) || '采购任务';
  const goal = text(packageItem.goal);
  const scope = text(packageItem.scope);
  const lines = [
    `# ${title}`,
    goal ? `目标：${goal}` : '',
    scope ? `范围：${scope}` : '',
    '',
    ...section('品类候选', listLines(packageItem.category_candidates)),
    ...section('预算候选', listLines(packageItem.budget_candidates)),
    ...section('数量候选', listLines(packageItem.quantity_candidates)),
    ...section('依赖关系', listLines(packageItem.dependencies)),
    ...section('待确认问题', listLines(packageItem.open_questions)),
    ...section('推荐字段包', fieldPackLines(packageItem.field_pack_suggestions)),
    ...section('后续动作', actionLines(packageItem.next_actions)),
  ];
  return lines.filter((line, index, source) => (
    line || (index > 0 && source[index - 1])
  )).join('\n').trim();
}

export function selectArtifactContentForTab(options = {}) {
  const {
    artifactDocument,
    tabKey,
    fallbackContent,
    versions,
  } = resolveObject(options);
  const revisionContent = latestRevisionContentForTab({
    artifactDocument,
    tabKey,
    versions,
  });
  if (revisionContent) {
    return revisionContent;
  }
  if (isArtifactPackageTabKey(tabKey)) {
    const packageItem = findPackageByTabKey(artifactDocument, tabKey);
    const markdown = packageMarkdown(packageItem);
    return firstText(markdown, fallbackContent);
  }
  return firstText(resolveObject(artifactDocument).content, fallbackContent);
}

export default selectArtifactContentForTab;
